import React, { useRef, useState, useEffect } from "react";
import {
    Worker, Viewer, SpecialZoomLevel, ScrollMode,
    RotateDirection, MinimalButton
} from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import Draggable from "react-draggable";
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/full-screen/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
import "react-tiff/dist/index.css";
import { rotatePlugin } from "@react-pdf-viewer/rotate";
import TIFFViewer from "./Customtiffviewer";
import { AiOutlineZoomIn } from "react-icons/ai";
import { BsZoomOut } from "react-icons/bs";
import { FaArrowRotateLeft } from "react-icons/fa6";
import { FaArrowRotateRight } from "react-icons/fa6";
import { BiReset } from "react-icons/bi";
import { FaChevronLeft } from "react-icons/fa";
import { FaChevronRight } from "react-icons/fa";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import LazyPdfViewer from "./LazyPdfViewer";


// File type detection utility
const getFileType = (url) => {
    if (!url) return 'unknown';
    const extension = url.split('.').pop()?.toLowerCase();
    const urlLower = url.toLowerCase();
    const typeMap = {
        pdf: ['pdf'],
        tiff: ['tiff', 'tif'],
        image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
    };
    for (const [type, extensions] of Object.entries(typeMap)) {
        if (extensions.includes(extension)) return type;
    }
    if (urlLower.includes('pdf')) return 'pdf';
    if (urlLower.includes('tiff') || urlLower.includes('tif')) return 'tiff';
    return 'unknown';
};

// Reusable error display component
const ErrorDisplay = ({ error, fileUrl, fileType = 'file' }) => {
    const errorConfig = {
        '404': {
            icon: fileType === 'pdf' ? 'bi-file-earmark-pdf' : 'bi-file-earmark-x',
            title: `404 - ${fileType.toUpperCase()} Not Found`,
            message: `The requested ${fileType} file could not be found.`,
            color: '#dc3545'
        },
        'NETWORK_ERROR': {
            icon: 'bi-wifi-off',
            title: 'Network Error',
            message: `Unable to load the ${fileType} file. Please check your connection.`
        },
        'default': {
            icon: 'bi-exclamation-triangle',
            title: `Error Loading ${fileType.toUpperCase()}`,
            message: `Failed to load ${fileType} file (${error})`
        }
    };
    const config = errorConfig[error] || errorConfig.default;
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            textAlign: 'center',
            color: '#666'
        }}>
            <i className={`bi ${config.icon}`} style={{ fontSize: '48px', marginBottom: '20px', color: config.color || '#666' }}></i>
            <h4>{config.title}</h4>
            <p>{config.message}</p>
            <small style={{ color: '#999' }}>URL: {fileUrl}</small>
        </div>
    );
};

// Reusable loading spinner component
const LoadingSpinner = ({ message = 'Loading...' }) => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%'
    }}>
        <div className="spinner-border" role="status">
            <span className="visually-hidden">{message}</span>
        </div>
    </div>
);

// Custom hook for file loading
const useFileLoader = (fileUrl) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fileData, setFileData] = useState(null);

    useEffect(() => {
        const loadFile = async () => {
            if (!fileUrl) return;
            try {
                setLoading(true);
                setError(null);
                const response = await fetch(fileUrl);
                if (response.status === 404) {
                    setError('404');
                    return;
                }
                if (!response.ok) {
                    setError(`HTTP ${response.status}`);
                    return;
                }
                const blob = await response.blob();
                const blobURL = URL.createObjectURL(blob);
                setFileData(blobURL);
            } catch (error) {
                console.error('Error loading file:', error);
                setError('NETWORK_ERROR');
            } finally {
                setLoading(false);
            }
        };
        loadFile();
    }, [fileUrl]);

    return { loading, error, fileData };
};

// PDF Toolbar configuration
const renderToolbar = (Toolbar) => (
    <Toolbar>
        {(slots) => {
            const {
                CurrentPageInput, EnterFullScreen,
                GoToFirstPage, GoToNextPage, GoToPreviousPage, GoToLastPage,
                NumberOfPages, Zoom, ZoomIn, ZoomOut
            } = slots;

            return (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    // justifyContent:'center',
                    alignItems:"center",
                }}>
                    <div className='d-flex align-items-center flex-row' style={{ marginLeft: '100px' }}>
                        <GoToFirstPage />
                        <GoToPreviousPage />
                        <CurrentPageInput /> / <NumberOfPages />
                        <GoToNextPage />
                        <GoToLastPage />
                    </div>
                    <div className='d-flex flex-row' style={{ marginLeft: '7rem' }}>
                        <ZoomOut />
                        {/* <Zoom /> */}
                        <ZoomIn />
                    </div>
                    <div style={{ marginLeft: 'auto' ,visibility:'hidden' }}>
                        <EnterFullScreen />
                    </div>
                </div>
            );
        }}
    </Toolbar>
);

// PDF Viewer Component
const PDFViewer = ({ fileUrl, isMaximized, toggleMaximize, isneedFullScreenButton }) => {
    const [currentPage, setCurrentPage] = useState(0);
    const { loading, error, fileData } = useFileLoader(fileUrl);
    const rotatePluginInstance = rotatePlugin();
    const { RotatePage } = rotatePluginInstance;
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        renderToolbar,
        toolbarPlugin: {
            pageNavigationPlugin: {
                enableShortcuts: true,
            },
        },
        fullScreenPlugin: {
            onEnterFullScreen: (zoom) => {
                zoom(SpecialZoomLevel.PageFit);
                defaultLayoutPluginInstance.toolbarPluginInstance.scrollModePluginInstance.switchScrollMode(ScrollMode.Wrapped);
            },
            onExitFullScreen: (zoom) => {
                zoom(SpecialZoomLevel.PageWidth);
                defaultLayoutPluginInstance.toolbarPluginInstance.scrollModePluginInstance.switchScrollMode(ScrollMode.Vertical);
            },
        },
        sidebarTabs: () => [], 
    });

    const handlePageRotate = (props, position) => {
        const direction = position === 'left' ? RotateDirection.Backward : RotateDirection.Forward;
        props.onRotatePage(currentPage, direction);
    };

    if (loading) return <LoadingSpinner message="Loading PDF..." />;
    if (error) return <ErrorDisplay error={error} fileUrl={fileUrl} fileType="pdf" />;

    return (
        <Worker workerUrl="/assets/js/pdf.worker.min.js">
            <LazyPdfViewer 
                fileUrl={fileData} 
                isMaximized={isMaximized} 
                toggleMaximize={toggleMaximize} 
                isneedFullScreenButton={isneedFullScreenButton}
            />
        </Worker>
    );
};

// ✅ Updated TIFF Viewer Component with Rotate Buttons
const TIFFViewerComponent = ({ fileUrl }) => {
    const { loading, error, fileData } = useFileLoader(fileUrl);

    const [rotation, setRotation] = useState(0);
    const [scale, setScale] = useState(1);
    const [pages, setPages] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);

    const rotateLeft = () => setRotation((prev) => (prev - 90 + 360) % 360);
    const rotateRight = () => setRotation((prev) => (prev + 90) % 360);
    const zoomIn = () => setScale((prev) => Math.min(prev + 0.1, 3));
    const zoomOut = () => setScale((prev) => Math.max(prev - 0.1, 0.3));
    const goPrev = () => setCurrentPage((prev) => Math.max(prev - 1, 0));
    const goNext = () => setCurrentPage((prev) => Math.min(prev + 1, pages.length - 1));

    const handleReset = () => {
        setScale(1);
        setRotation(0);
        setCurrentPage(0);
    };

    if (loading) return <LoadingSpinner message="Loading TIFF..." />;
    if (error) return <ErrorDisplay error={error} fileUrl={fileUrl} fileType="tiff" />;

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div
                style={{
                    padding: '6px',
                    backgroundColor: 'rgb(3, 150, 166)'
                }}
            >

                <div style={{ paddingRight:"25px",paddingLeft:"25px", display: 'flex', gap: 10, flexWrap: 'wrap' ,justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{gap:"12px",display:"flex"}} >
                        <button onClick={rotateLeft}><FaArrowRotateLeft /></button>
                        <button onClick={rotateRight}><FaArrowRotateRight /></button>
                        <button onClick={zoomIn}><AiOutlineZoomIn /></button>
                        <button onClick={zoomOut}><BsZoomOut /></button>
                        <button onClick={handleReset}><BiReset /></button>
                    </div>
                    <div style={{gap:"12px",display:"flex"}} >
                        <button onClick={goPrev} disabled={currentPage === 0}><FaChevronLeft /></button>
                        <span style={{ color: "white" }} >Page {currentPage + 1} of {pages.length}</span>
                        <button onClick={goNext} disabled={currentPage === pages.length - 1}><FaChevronRight />
                        </button>
                    </div>
                    <div></div>
                </div>
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
                <TIFFViewer
                    setCurrentPage={setCurrentPage}
                    rotation={rotation}
                    setRotation={setRotation}
                    scale={scale}
                    setScale={setScale}
                    fileUrl={fileData}
                    setPages={setPages}
                    pages={pages}
                    currentPage={currentPage}
                />
            </div>
        </div>
    );
};


// Image Viewer Component
const ImageViewer = ({ fileUrl }) => (
    <div style={{ textAlign: 'center', padding: '20px' }}>
        <img src={fileUrl} alt="Image" style={{ maxWidth: '100%', maxHeight: '70vh' }} />
    </div>
);

// Unsupported File Display
const UnsupportedFile = ({ fileUrl }) => (
    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        <i className="bi bi-file-earmark-x" style={{ fontSize: '48px' }}></i>
        <p>Unsupported file type</p>
        <small>File: {fileUrl}</small>
    </div>
);

// Main File Viewer Modal Component
const FileViewerModal = ({ 
    fileUrl, 
    showFileView, 
    setShowFileView, 
    isMaximized: externalIsMaximized, 
    toggleMaximize: externalToggleMaximize ,
    isneedFullScreenButton,
    bottomComponent
}) => {
    const [internalIsMaximized, setInternalIsMaximized] = useState(false);
    
    const isMaximized = externalIsMaximized !== undefined ? externalIsMaximized : internalIsMaximized;
    const toggleMaximize = externalToggleMaximize || (() => setInternalIsMaximized(!internalIsMaximized));

    const [fileType, setFileType] = useState('unknown');
    const [loading, setLoading] = useState(true);
    const modalRef = useRef(null);

    // Attempt to clear parent transform when maximized to fix Draggable issues
    useEffect(() => {
        if (isMaximized && modalRef.current) {
            const parent = modalRef.current.parentElement;
            if (parent && parent.style.transform && parent.style.transform !== 'none') {
                parent.style.transform = 'none';
            }
        }
    }, [isMaximized]);

    // Handle Escape key for un-maximizing and closing
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (isMaximized) {
                    toggleMaximize();
                } else {
                    setShowFileView(false);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [isMaximized, toggleMaximize, setShowFileView]);

    // Auto-focus the modal when it opens to ensure it catches keyboard events
    useEffect(() => {
        if (showFileView && modalRef.current) {
            modalRef.current.focus();
        }
    }, [showFileView]);

    useEffect(() => {
        if (fileUrl) {
            setFileType(getFileType(fileUrl));
            setLoading(false);
        }
    }, [fileUrl]);

    const renderFileViewer = () => {
        if (loading) return <LoadingSpinner />;
        const viewerComponents = {
            pdf: PDFViewer,
            tiff: TIFFViewerComponent,
            image: ImageViewer
        };
        const ViewerComponent = viewerComponents[fileType];
        return ViewerComponent ?
            <ViewerComponent 
                fileUrl={fileUrl} 
                isMaximized={isMaximized} 
                toggleMaximize={toggleMaximize} 
                isneedFullScreenButton={isneedFullScreenButton}
            /> :
            <UnsupportedFile fileUrl={fileUrl} />;
    };

    return (
            <div 
                ref={modalRef}
                tabIndex={0}
                style={{
                outline: 'none',
                position: 'fixed',
                top: externalIsMaximized === undefined ?'50px': isMaximized ? '0' : '350px',
                left: externalIsMaximized === undefined ?   '50px': isMaximized ? '0' : '250px',
                right: externalIsMaximized === undefined ?   '50px': isMaximized ? '0' : '50px',
                bottom: externalIsMaximized === undefined ? '50px' : isMaximized ? '0' : '50px',
                backgroundColor: 'white',
                borderRadius: isMaximized ? '0' : '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                zIndex: 999999999999999,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                // maxHeight: isMaximized ? '100vh' : '80vh',
                // maxWidth: isMaximized ? '100vw' : '70vw',
                transform: isMaximized ? 'none' : undefined // Attempt to override Draggable transform
            }}>
                <div className={` modal-custome-header ${isMaximized ? "" : "drag-handle"}`} style={{   // drag-handle is for the  draggable handle for reson code module image view 
                    padding: '10px 15px',
                    borderBottom: '1px solid #dee2e6',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#f8f9fa'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <span style={{ fontWeight: 'bold' }}>File Viewer ({fileType.toUpperCase()})</span>
                    </div>
                    <span style={{ cursor: 'pointer', fontSize: '20px' }} onClick={() => setShowFileView(false)} className='close-icon'>
                        <i className="bi bi-x-square"></i>
                    </span>
                </div>
                <div key={isMaximized ? 'maximized' : 'normal'} style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {bottomComponent}
                    <div style={{ flex: 1, overflow: 'hidden', minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
                        {renderFileViewer()}
                    </div>
                </div>
            </div>
    );
};

export default FileViewerModal;



